import { Coffee, Sandwich, Flame, ListChecks, FilePlus2 } from "lucide-react";

export type Template = { key: string; label: string; prefix: string; kind: string; icon: any;
  namePh: string; detailsPh: string; dictateHint: string };
export type Preset = { templates: Template[]; tips: [string, string][]; askExample: string; suggest: string[];
  captureTitlePh: string; captureNotePh: string; specialIcon: any; specialHint: string };

const RECORD_TIP: [string, string] = ["Try: tap the mic and explain how you close up", "record"];

const NEUTRAL: Preset = {
  templates: [{ key: "procedure", label: "New procedure", prefix: "New procedure", kind: "", icon: FilePlus2,
    namePh: "Name, e.g. Opening routine", detailsPh: "Everything else, in any order: steps, amounts, tips, warnings… SMBOI will organize and double-check it.",
    dictateHint: "Tap the mic and explain it — I'll type it in." }],
  tips: [["Try: add a new procedure", "template"], ["Try: “What are the opening steps?”", "ask"], RECORD_TIP],
  askExample: "What are the opening steps?",
  suggest: ["What are the steps?", "What do I need?", "Who do I ask if something goes wrong?"],
  captureTitlePh: "e.g. Opening routine", captureNotePh: "Steps, amounts, tips…", specialIcon: FilePlus2,
  specialHint: "Reply “special” + the details and I'll draft it for you.",
};

const PRESETS: Record<string, Preset> = {
  coffee_shop: {
    templates: [{ key: "drink", label: "New drink", prefix: "New drink", kind: "drink", icon: Coffee,
      namePh: "Name, e.g. Honey oat cortado", detailsPh: "Everything else, in any order: ingredients, amounts, steps, tips, allergens… SMBOI will organize and double-check it.",
      dictateHint: "Tap the mic and say the recipe — I'll type it in." }],
    tips: [["Try: record today's special", "drink"], ["Try: “What are the steps for the vanilla latte?”", "ask"], RECORD_TIP,
      ["Try: add a new drink with the recipe card", "drink"]],
    askExample: "What are the steps for the vanilla latte?",
    suggest: ["What ingredients do I need?", "What are the steps?", "Can I substitute anything?"],
    captureTitlePh: "e.g. House oat latte", captureNotePh: "Ingredients, amounts, steps…", specialIcon: Coffee,
    specialHint: "Reply “special” + the drink details and I'll draft it for you.",
  },
  fast_food: {
    templates: [
      { key: "menu_item", label: "New menu item", prefix: "New menu item", kind: "drink", icon: Sandwich,
        namePh: "Name, e.g. Krabby Patty", detailsPh: "Build order, cook times, toppings, portion sizes, packaging, allergens… in any order. SMBOI will organize and double-check it.",
        dictateHint: "Tap the mic and walk through the build — I'll type it in." },
      { key: "station", label: "Kitchen station guide", prefix: "Kitchen station guide", kind: "", icon: Flame,
        namePh: "Station, e.g. Grill or Fryer", detailsPh: "Settings, temperatures, timers, holding times, cleaning and safety steps…",
        dictateHint: "Tap the mic and explain the station — I'll type it in." },
      { key: "checklist", label: "Shift checklist", prefix: "Shift checklist", kind: "", icon: ListChecks,
        namePh: "Checklist, e.g. Opening shift", detailsPh: "Every step in order, plus anything to gather or check first…",
        dictateHint: "Tap the mic and list the steps — I'll type them in." },
    ],
    tips: [["Try: add a new menu item with its build order", "menu_item"], ["Try: “How long do the patties cook?”", "ask"],
      ["Try: write the opening shift checklist", "checklist"], ["Try: explain how the fryer is set up", "station"]],
    askExample: "How long do the patties cook?",
    suggest: ["How long does it cook?", "What goes on it, in order?", "How do I clean the grill?"],
    captureTitlePh: "e.g. Krabby Patty build", captureNotePh: "Build order, cook times, steps…", specialIcon: Sandwich,
    specialHint: "Reply “special” + the menu item details and I'll draft it for you.",
  },
};

export const presetFor = (key?: string): Preset => PRESETS[key || ""] || NEUTRAL;
